import { useRouter } from 'next/router';
import React, {
  FunctionComponent, useCallback, useMemo, useState,
} from 'react';
import { PropertyStatus } from '../../../domain/propertyStatus';
import { PropertyType } from '../../../domain/propertyType';
import { Lead } from '../../../services/dtos/lead';
import { createEvaluationLeadV2 } from '../../../services/leads/leads';
import LeadForm, { LeadFormModel } from '../../components/lead-form/LeadForm';
import { GoogleMapsContext } from '../../contexts/google-map-context/GoogleMapsContext';
import useAutoCompleteSessionToken from '../../hooks/useAutoCompleteSessionToken/useAutoCompleteSessionToken';
import useNextConfig from '../../hooks/useNextConfig/useNextConfig';
import { getPlaceDetails, PlaceDetails } from '../../services/geo/geo';
import getCookie from '../../utils/cookie/getCookie';
import getTrafficSource from '../../utils/traffic-source/getTrafficSource';

export interface CreateLeadProps {
  source?: string,
  campaign?: string,
  dataRef?: string,
  dataTracking?: string,
  onSuccess?: (lead: Lead) => void,
}

const ERROR_MESSAGE = 'Errore durante il salvataggio, riprova o contattaci!';

const CreateLead: FunctionComponent<CreateLeadProps> = ({
  source,
  campaign,
  dataRef,
  dataTracking,
  onSuccess,
}) => {
  const { MAPS } = useNextConfig();
  const router = useRouter();
  const { data: sessionToken } = useAutoCompleteSessionToken(MAPS?.apiKey);

  const googleMapsContext = useMemo(() => ({
    apiKey: MAPS?.apiKey,
    sessionToken,
  }), [MAPS, sessionToken]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();

  const onSubmit = useCallback(async (values: LeadFormModel) => {
    setLoading(true);

    try {
      let place: PlaceDetails | undefined;
      const trafficSource = source || getTrafficSource(router.query);

      if (MAPS && values.placeId) {
        place = await getPlaceDetails(MAPS.apiKey, values.placeId, { sessionToken });
      }

      const lead = await createEvaluationLeadV2({
        id: null,
        name: values.name,
        phoneNumber: values.phoneNumber,
        email: values.email ?? null,
        propertyAddress: values.address,
        propertySize: 0,
        propertyFeatures: {},
        propertyFloor: null,
        propertyStatus: PropertyStatus.ABITABILE,
        propertyType: PropertyType.APPARTAMENTO,
        source: trafficSource,
        consent: values.consent,
        locality: place?.locality ?? null,
        plateCode: place?.plateCode ?? null,
        postalCode: place?.postalCode ?? null,
        latitude: place?.latitude,
        longitude: place?.longitude,
        gclid: router.query.gclid as string ?? null,
        utmCampaign: router.query.utm_campaign as string || campaign || null,
        originUrl: window.location.href,
        fbc: getCookie('_fbc'),
      });

      if (onSuccess) {
        onSuccess(lead);
      }
    } catch (err) {
      setError(err);
    }

    setLoading(false);
  }, [source, router.query, MAPS, campaign, onSuccess, sessionToken]);

  return (
    <GoogleMapsContext.Provider value={googleMapsContext}>
      <LeadForm
        dataRef={dataRef}
        dataTracking={dataTracking}
        onSubmit={onSubmit}
        loading={loading}
        errorMessage={error && ERROR_MESSAGE}
      />
    </GoogleMapsContext.Provider>
  );
};

export default CreateLead;
